import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { AlertTriangle, Loader2 } from 'lucide-react';

/**
 * <ConfirmDestructive />
 *
 * Wraps a destructive action (pause tenant, refund, kill switch, delete)
 * with a confirmation dialog that REQUIRES a reason. Mirrors what Stripe
 * Connect / AWS Console / Anthropic admin enforce.
 *
 * Usage:
 *   <ConfirmDestructive
 *     trigger={<Button variant="destructive">Pause</Button>}
 *     title="Pause tenant?"
 *     description="This stops all AI workers for this tenant immediately."
 *     confirmLabel="Pause tenant"
 *     onConfirm={async (reason) => { await api.pauseTenant(id, reason); }}
 *   />
 */
export default function ConfirmDestructive({
  trigger,
  open: controlledOpen,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  reasonRequired = true,
  reasonPlaceholder = 'Why are you taking this action? (audited)',
  onConfirm,
  testId = 'gov-confirm-destructive',
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (onOpenChange || (() => {})) : setInternalOpen;

  const canConfirm = !reasonRequired || reason.trim().length >= 3;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm?.(reason.trim());
      setOpen(false);
      setReason('');
    } catch (_e) {
      // surface upstream; modal stays open so user can retry
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && trigger && (
        <span onClick={() => setOpen(true)} data-testid={`${testId}-trigger`}>
          {trigger}
        </span>
      )}
      <DialogContent className="sm:max-w-[460px]" data-testid={testId}>
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2">
            <div className="rounded-full bg-rose-500/10 p-2">
              <AlertTriangle className="h-4 w-4 text-rose-500" />
            </div>
            <DialogTitle data-testid={`${testId}-title`}>{title}</DialogTitle>
          </div>
          {description && (
            <DialogDescription className="leading-relaxed" data-testid={`${testId}-description`}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        {reasonRequired && (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-reason" className="text-xs">
              Reason <span className="text-rose-500">*</span>
            </Label>
            <Textarea
              id="confirm-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              rows={3}
              className="text-sm"
              data-testid={`${testId}-reason`}
              autoFocus
            />
            <p className="gov-text-tertiary text-[11px]">
              Recorded in the audit log with your identity and timestamp.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={submitting}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm || submitting}
            data-testid={`${testId}-confirm`}
          >
            {submitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
